import React, { useState, useEffect, useRef } from "react";
import Chart from "chart.js/auto";
import { 
    Box, 
    Tabs, 
    Tab, 
    Card, 
    CardContent, 
    Typography, 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow, 
    Paper, 
    Chip, 
    Avatar 
  } from "@mui/material";
  import { 
    Inventory, 
    AttachMoney, 
    CalendarToday 
  } from "@mui/icons-material";
import moment from "moment";

const AnalyticsTabs = ({ value, handleTabChange, data }) => {
  const chartRef = useRef(null);
  const [chartInstance, setChartInstance] = useState(null);

  useEffect(() => {
    if (data.costSummary.length && chartRef.current) {
      const ctx = chartRef.current.getContext("2d");
      const validCostSummary = data.costSummary.filter(item => item !== null && item !== undefined);
      const allCategories = [...new Set(validCostSummary.flatMap(item => Object.keys(item.categoryCosts || {})))];
      
      if (ctx && !chartInstance) {
        const datasets = [
          { 
            label: "Total Cost ($)", 
            data: validCostSummary.map(item => item.totalCost || 0), 
            backgroundColor: "rgba(59, 130, 246, 0.8)", 
            borderColor: "#3b82f6", 
            borderWidth: 2, 
            borderRadius: 8,
            borderSkipped: false,
          },
          ...allCategories.map((category, index) => ({
            label: `${category} Cost ($)`,
            data: validCostSummary.map(item => item.categoryCosts[category] || 0),
            backgroundColor: `hsla(${index * 60}, 70%, 50%, 0.8)`,
            borderColor: `hsl(${index * 60}, 70%, 40%)`,
            borderWidth: 2,
            borderRadius: 8,
            borderSkipped: false,
          })),
        ];
        
        const newChartInstance = new Chart(ctx, {
          type: "bar",
          data: { labels: validCostSummary.map(item => item._id), datasets },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: { 
              legend: { 
                position: "top",
                labels: {
                  color: '#ffffff',
                  font: { size: 12 },
                  usePointStyle: true,
                  padding: 15
                }
              }, 
              tooltip: { 
                mode: "index",
                backgroundColor: 'rgba(0,0,0,0.8)',
                titleColor: '#ffffff',
                bodyColor: '#ffffff',
                borderColor: '#3b82f6',
                borderWidth: 1
              } 
            },
            scales: { 
              y: { 
                beginAtZero: true,
                ticks: { color: '#ffffff' },
                grid: { color: 'rgba(255,255,255,0.1)' }
              }, 
              x: { 
                ticks: { 
                  maxRotation: 45,
                  color: '#ffffff'
                },
                grid: { color: 'rgba(255,255,255,0.1)' }
              } 
            },
          },
        });
        setChartInstance(newChartInstance);
      } else if (chartInstance) {
        chartInstance.data.labels = validCostSummary.map(item => item._id);
        chartInstance.data.datasets = [
          { 
            label: "Total Cost ($)", 
            data: validCostSummary.map(item => item.totalCost || 0), 
            backgroundColor: "rgba(59, 130, 246, 0.8)", 
            borderColor: "#3b82f6", 
            borderWidth: 2, 
            borderRadius: 8,
            borderSkipped: false,
          },
          ...allCategories.map((category, index) => ({
            label: `${category} Cost ($)`,
            data: validCostSummary.map(item => item.categoryCosts[category] || 0),
            backgroundColor: `hsla(${index * 60}, 70%, 50%, 0.8)`,
            borderColor: `hsl(${index * 60}, 70%, 40%)`,
            borderWidth: 2,
            borderRadius: 8,
            borderSkipped: false,
          })),
        ];
        chartInstance.update();
      }
    } else if (chartInstance && !data.costSummary.length) {
      chartInstance.destroy();
      setChartInstance(null);
    }
    return () => chartInstance?.destroy();
  }, [data.costSummary, chartInstance, chartRef]);

  return (
    <Box sx={{ width: "100%" }}>
      <Tabs 
        value={value} 
        onChange={handleTabChange} 
        aria-label="Cost & Inventory Tabs"
        sx={{
          '& .MuiTabs-indicator': { backgroundColor: '#3b82f6' },
          '& .MuiTab-root': { 
            color: 'rgba(255, 255, 255, 0.7)',
            fontWeight: 'bold',
            fontSize: '1rem'
          },
          '& .Mui-selected': { color: '#ffffff !important' }
        }}
      >
        <Tab 
          label="Cost Summary" 
          icon={<AttachMoney />} 
          iconPosition="start"
          sx={{ minHeight: '64px' }}
        />
        <Tab 
          label="Inventory Utilization" 
          icon={<Inventory />} 
          iconPosition="start"
          sx={{ minHeight: '64px' }}
        />
      </Tabs>
      
      <Box sx={{ mt: 3 }}>
        {value === 0 && (
          <Card sx={{ 
            bgcolor: 'rgba(255, 255, 255, 0.05)', 
            border: '1px solid rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)'
          }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: '#ffffff', mb: 3, fontWeight: 'bold' }}>
                Cost Summary Analysis
              </Typography>
              <Box sx={{ height: "450px", mb: 4, p: 2, bgcolor: 'rgba(0,0,0,0.2)', borderRadius: 2 }}>
                <canvas ref={chartRef} id="costChart" />
              </Box>
              <TableContainer component={Paper} sx={{ 
                bgcolor: 'rgba(255, 255, 255, 0.05)', 
                border: '1px solid rgba(255, 255, 255, 0.1)' 
              }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ '& th': { bgcolor: 'rgba(59, 130, 246, 0.2)', color: '#ffffff', fontWeight: 'bold' } }}>
                      <TableCell>Product Type</TableCell>
                      <TableCell align="center">Orders</TableCell>
                      <TableCell align="center">Total Cost</TableCell>
                      {data.costSummary[0]?.categoryCosts && Object.keys(data.costSummary[0].categoryCosts).map((cat, i) => (
                        <TableCell key={i} align="center">{cat}</TableCell>
                      ))}
                      <TableCell align="center">Avg/Order</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.costSummary
                      .filter(item => item !== null && item !== undefined)
                      .map((item, index) => (
                        <TableRow 
                          key={item._id}
                          sx={{ 
                            '& td': { color: '#ffffff', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' },
                            '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.05)' }
                          }}
                        >
                          <TableCell>
                            <Chip 
                              label={item._id} 
                              sx={{ 
                                bgcolor: `hsl(${index * 60}, 70%, 50%)`,
                                color: '#ffffff',
                                fontWeight: 'bold'
                              }} 
                            />
                          </TableCell>
                          <TableCell align="center">{item.totalOrders?.toLocaleString()}</TableCell>
                          <TableCell align="center">${item.totalCost?.toLocaleString()}</TableCell>
                          {Object.values(item.categoryCosts || {}).map((cost, i) => (
                            <TableCell key={i} align="center">${cost?.toLocaleString()}</TableCell>
                          ))}
                          <TableCell align="center">${item.avgOrderCost?.toLocaleString()}</TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}
        
        {value === 1 && (
          <Card sx={{ 
            bgcolor: 'rgba(255, 255, 255, 0.05)', 
            border: '1px solid rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)'
          }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: '#ffffff', mb: 3, fontWeight: 'bold' }}>
                Inventory Utilization Report
              </Typography>
              <TableContainer component={Paper} sx={{ 
                bgcolor: 'rgba(255, 255, 255, 0.05)', 
                border: '1px solid rgba(255, 255, 255, 0.1)' 
              }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ '& th': { bgcolor: 'rgba(139, 92, 246, 0.2)', color: '#ffffff', fontWeight: 'bold' } }}>
                      <TableCell>Item Name</TableCell>
                      <TableCell align="center">Total Quantity Used</TableCell>
                      <TableCell align="center">Last Utilized</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.inventoryUtilization.map((item, index) => (
                      <TableRow 
                        key={item._id}
                        sx={{ 
                          '& td': { color: '#ffffff', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' },
                          '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.05)' }
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar sx={{ bgcolor: '#8b5cf6', mr: 2, width: 32, height: 32 }}>
                              <Inventory fontSize="small" />
                            </Avatar>
                            {item.itemName}
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={item.totalQuantityUsed?.toLocaleString()} 
                            sx={{ 
                              bgcolor: 'rgba(16, 185, 129, 0.2)',
                              color: '#10b981',
                              fontWeight: 'bold'
                            }} 
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <CalendarToday sx={{ mr: 1, fontSize: 16, color: 'rgba(255, 255, 255, 0.7)' }} />
                            {moment(item.lastUtilized).format("MMM DD, YYYY")}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}
      </Box>
    </Box>
  );
};

export default AnalyticsTabs;